import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import {
  Clock,
  Radio,
  CheckCircle2,
  FileText,
  Code2,
  HelpCircle,
  BookOpen,
  Lock,
  PlayCircle,
  Download,
  MessageCircle,
  BookMarked,
  ArrowRight,
  ExternalLink,
  Maximize2,
  Package,
} from "lucide-react";
import {
  fetchSessionDetail,
  pingSessionAttendance,
  updateSessionProgress,
  emitResourceEvent,
} from "../../../utils/smartLearning";
import { usePlan } from "../../../contexts/PlanContext";
import { AppModal } from "../../ui";
import useMaterialViewer from "../../Materials/useMaterialViewer";
import ScormPlayer from "../../SelfPaced/ScormPlayer";
import SessionStateBadge from "./SessionStateBadge";

const ATTENDANCE_PING_MS = 60000;

// One hue per resource KIND — a category map, so the chart ramp. `token` rather
// than a colour because the tint below was built by string-concatenating an
// alpha onto a hex (`${color}18`), which is what made a literal look load-bearing.
const KIND_META = {
  document: { Icon: BookOpen, label: "Document", token: "--chart-1" },
  assignment: { Icon: FileText, label: "Assignment", token: "--chart-5" },
  codingChallenge: { Icon: Code2, label: "Coding Challenge", token: "--chart-2" },
  quiz: { Icon: HelpCircle, label: "Quiz", token: "--chart-3" },
  scorm: { Icon: Package, label: "Interactive (SCORM)", token: "--chart-6" },
};

function formatDuration(minutes) {
  if (!minutes) return null;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
}

/**
 * InlineSessionDetail — center-panel session detail for the 3-column learning layout.
 * Replaces the offcanvas drawer with an always-visible panel.
 */
const InlineSessionDetail = ({ sessionId, sessionState, selectedSession, aivoOnly = false }) => {
  const navigate = useNavigate();
  const { hasPlanFeature } = usePlan();
  // With `aivoOnly` there is no Ask AI Doubt without AIvo: Learn with AI is practice, not help.
  const showAsk = !aivoOnly || hasPlanFeature("aiDoubtSolving");
  // In-app document viewer (PDF/PPT/DOCX/image rendered inline) — replaces the
  // old new-tab link to /s/materials/:id (which isn't a real route).
  const { openMaterial, viewer } = useMaterialViewer();
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [session, setSession] = useState(null);
  const [progress, setProgress] = useState(null);
  const [showRecording, setShowRecording] = useState(false);
  const [scormLesson, setScormLesson] = useState(null); // refId of SCORM resource being played
  const reportedPctRef = useRef(0);

  // Re-pull the session (refreshes resource done-state + progress) after the
  // student finishes a SCORM package.
  const refreshDetail = () => {
    if (!sessionId) return;
    fetchSessionDetail(sessionId)
      .then((data) => {
        setSession(data.session);
        setProgress(data.progress || null);
      })
      .catch(() => { /* keep current view on a transient error */ });
  };

  const handleScormComplete = () => {
    if (!scormLesson) return;
    emitResourceEvent({ kind: "scorm", refId: scormLesson });
    refreshDetail();
  };

  // Open AIvo (AI doubt assistant) seeded with this session's context so the
  // model's answer is grounded in the right course → module → session.
  const askAiDoubt = () => {
    const crumbs = [selectedSession?.moduleTitle, session?.title].filter(Boolean);
    const prefill = crumbs.length
      ? `I have a doubt about "${crumbs.join(" → ")}". `
      : "";
    if (hasPlanFeature("aiDoubtSolving")) {
      window.dispatchEvent(
        new CustomEvent("vc-open-aivo", {
          detail: {
            prefill,
            context: {
              courseId: session?.course,
              moduleNumber: session?.moduleNumber,
              sessionId: session?._id,
            },
          },
        }),
      );
    } else {
      navigate("/s/learn-with-ai");
    }
  };

  useEffect(() => {
    if (!sessionId) {
      setSession(null);
      setProgress(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setErr("");
    setShowRecording(false);
    fetchSessionDetail(sessionId)
      .then((data) => {
        if (cancelled) return;
        setSession(data.session);
        setProgress(data.progress || null);
      })
      .catch((e) => {
        if (cancelled) return;
        setErr(e?.response?.data?.message || "Failed to load session.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  // Attendance heartbeat while drawer is open on a live session
  useEffect(() => {
    if (!sessionId || session?.status !== "live") return undefined;
    let active = true;
    const tick = () => {
      if (active) pingSessionAttendance(sessionId, 1);
    };
    tick();
    const id = setInterval(tick, ATTENDANCE_PING_MS);
    return () => {
      active = false;
      clearInterval(id);
    };
  }, [sessionId, session?.status]);

  // Seed monotonic baseline from saved progress
  useEffect(() => {
    reportedPctRef.current = progress?.recordingWatchedPercent || 0;
  }, [progress?.recordingWatchedPercent, sessionId]);

  const handleRecordingTimeUpdate = (e) => {
    const v = e.currentTarget;
    if (!v.duration || Number.isNaN(v.duration)) return;
    const pct = Math.min(100, Math.round((v.currentTime / v.duration) * 100));
    if (pct >= reportedPctRef.current + 5 || (pct >= 95 && reportedPctRef.current < 95)) {
      reportedPctRef.current = pct;
      updateSessionProgress(sessionId, { recordingWatchedPercent: pct }).then((p) => {
        if (p) setProgress(p);
      });
    }
  };

  // Empty state
  if (!sessionId) {
    return (
      <div className="lp-detail lp-detail--empty">
        <div className="lp-detail__empty-icon">📚</div>
        <h3 className="lp-detail__empty-title">Select a session to begin</h3>
        <p className="lp-detail__empty-desc">
          Choose a session from the left to view details, resources, and track your progress.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="lp-detail lp-detail--loading">
        <Spinner animation="border" size="sm" />
        <span>Loading session…</span>
      </div>
    );
  }

  if (err) {
    return <div className="lp-detail lp-detail--error">{err}</div>;
  }

  if (!session) return null;

  const isLocked = sessionState === "locked";
  const isLive = session.status === "live";
  const progressPct = progress?.progressPercent || 0;
  const hasRecording = !!session.recordingUrl;

  // Meta
  const sessionDate = session.startedAt
    ? new Date(session.startedAt).toLocaleDateString("en-US", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : null;
  const duration = formatDuration(session.plannedDurationMinutes);

  // Class notes → first document resource
  const firstDoc = (session.resources || []).find((r) => r.kind === "document");

  // SCORM packages are heterogeneous siblings inside a session; the in-app
  // player can only render SCORM, so "Next" advances to the next SCORM package
  // in display order (skipping quizzes/assignments the student does from the
  // list). All resources share one session-level unlock gate, so any sibling
  // SCORM is openable once the current one is.
  const scormResources = (session.resources || []).filter(
    (r) => r.kind === "scorm",
  );
  const currentScormIdx = scormResources.findIndex(
    (r) => r.refId === scormLesson,
  );
  const nextScorm =
    currentScormIdx >= 0 ? scormResources[currentScormIdx + 1] : null;

  return (
    <div className="lp-detail">
      {/* ── Hero card ─────────────────────────────────────────────────── */}
      <div className={`lp-detail__hero ${isLive ? "lp-detail__hero--live" : ""}`}>
        <div className="lp-detail__hero-left">
          <div className="lp-detail__hero-badge-row">
            <SessionStateBadge state={sessionState || "available"} />
            {!isLocked && session?.recordingVideoId && (
              <button
                type="button"
                className="lp-detail__fullpage-link"
                onClick={() => navigate(`/s/watch-v2/${session.recordingVideoId}`)}
                title="Open full session page"
              >
                <Maximize2 size={12} /> Open full page
              </button>
            )}
          </div>
          <h1 className="lp-detail__hero-title">{session.title}</h1>

          {(sessionDate || duration) && (
            <div className="lp-detail__hero-meta">
              {sessionDate && (
                <span className="lp-detail__meta-item">
                  <span>📅</span> {sessionDate}
                </span>
              )}
              {duration && (
                <span className="lp-detail__meta-item">
                  <Clock size={13} /> {duration}
                </span>
              )}
              {isLive && (
                <span className="lp-detail__meta-item lp-detail__meta-item--live">
                  <Radio size={12} /> Live now
                </span>
              )}
            </div>
          )}

          {session.description && (
            <p className="lp-detail__hero-desc">{session.description}</p>
          )}

          {isLive && session.meetingJoinUrl && (
            <a
              href={session.meetingJoinUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="lp-detail__join-btn"
            >
              <Radio size={15} /> Join Live Class
            </a>
          )}
        </div>
      </div>

      {/* ── Action buttons ───────────────────────────────────────────── */}
      {!isLocked && (
        <div className="lp-detail__actions">
          {hasRecording && (
            <button
              type="button"
              className="lp-detail__action-btn lp-detail__action-btn--primary"
              onClick={() => setShowRecording((v) => !v)}
            >
              <PlayCircle size={15} />
              {showRecording ? "Hide Recording" : "Continue Watching"}
            </button>
          )}
          {firstDoc ? (
            <button
              type="button"
              className="lp-detail__action-btn"
              onClick={() =>
                openMaterial({ _id: firstDoc.refId, title: firstDoc.title, fileType: firstDoc.fileType })
              }
            >
              <BookMarked size={15} /> Class Notes
            </button>
          ) : (
            <button type="button" className="lp-detail__action-btn" disabled>
              <BookMarked size={15} /> Class Notes
            </button>
          )}
          <button type="button" className="lp-detail__action-btn">
            <Download size={15} /> Download Materials
          </button>
          {showAsk && (
            <button type="button" className="lp-detail__action-btn" onClick={askAiDoubt}>
              <MessageCircle size={15} /> Ask AI Doubt
            </button>
          )}
        </div>
      )}

      {/* ── Inline recording player ──────────────────────────────────── */}
      {showRecording && session.recordingUrl && (
        <div className="lp-detail__recording">
          <div className="lp-detail__rec-head">
            <span className="lp-detail__rec-label">
              <PlayCircle size={13} /> Session Recording
            </span>
            {progress?.recordingWatchedPercent > 0 && (
              <span className="lp-detail__rec-pct">
                {progress.recordingWatchedPercent}% watched
              </span>
            )}
          </div>
          <video
            className="lp-detail__video"
            src={session.recordingUrl}
            controls
            preload="metadata"
            onTimeUpdate={handleRecordingTimeUpdate}
            onEnded={handleRecordingTimeUpdate}
          />
          <a
            href={session.recordingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="lp-detail__rec-ext"
          >
            <ExternalLink size={11} /> Open in new tab
          </a>
        </div>
      )}

      {/* ── Locked state ─────────────────────────────────────────────── */}
      {isLocked ? (
        <div className="lp-detail__locked-panel">
          <Lock size={20} />
          <div>
            <h5>Complete the previous session to unlock</h5>
            <p>
              Finish the prior session's class, assignments, or quiz to open this
              one.
            </p>
          </div>
        </div>
      ) : (
        <>
          {/* ── What's Unlocked ───────────────────────────────────────── */}
          <div className="lp-detail__unlocked">
            <div className="lp-detail__unlocked-head">
              <h3 className="lp-detail__section-title">What's Unlocked</h3>
              {(session.resources || []).length > 0 && (
                <p className="lp-detail__section-sub">
                  Complete the below tasks to strengthen your learning
                </p>
              )}
            </div>

            {(session.resources || []).length === 0 ? (
              <p className="lp-detail__no-resources">
                No resources linked yet — your trainer will add assignments and
                materials for this session.
              </p>
            ) : (
              <ul className="lp-detail__resources">
                {session.resources.map((r, idx) => {
                  const meta = KIND_META[r.kind] || KIND_META.document;
                  const { Icon } = meta;
                  const unlocked =
                    session.resourcesUnlocked || session.status === "completed";
                  // SCORM carries its own per-resource progress (from the
                  // student's ScormAttempt), so it's done when that package is
                  // completed — not the shared documentsViewed counter.
                  const scormInProgress =
                    r.kind === "scorm" &&
                    r.scormProgress?.status === "in_progress" &&
                    !r.scormProgress?.completed;
                  const done =
                    (r.kind === "assignment" && progress?.assignmentCompleted) ||
                    (r.kind === "codingChallenge" && progress?.codingCompleted) ||
                    (r.kind === "quiz" && progress?.quizCompleted) ||
                    (r.kind === "scorm" && r.scormProgress?.completed) ||
                    (r.kind === "document" && (progress?.documentsViewed || 0) > 0);

                  return (
                    <li
                      key={`${r.refId}-${idx}`}
                      className={`lp-detail__res ${done ? "lp-detail__res--done" : ""} ${!unlocked ? "lp-detail__res--locked" : ""}`}
                    >
                      <span
                        className="lp-detail__res-icon"
                        style={{
                          background: `hsl(var(${meta.token}) / 0.09)`,
                          color: `hsl(var(${meta.token}))`,
                        }}
                      >
                        <Icon size={16} />
                      </span>
                      <div className="lp-detail__res-body">
                        <span className="lp-detail__res-title">
                          {r.title || meta.label}
                        </span>
                        <span className="lp-detail__res-sub">{meta.label}</span>
                        {r.dueDate && (
                          <span className="lp-detail__res-due">
                            Due:{" "}
                            {new Date(r.dueDate).toLocaleDateString("en-US", {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            })}
                          </span>
                        )}
                        {scormInProgress && (
                          <span className="lp-detail__res-due">
                            {r.scormProgress.progressPct != null
                              ? `${r.scormProgress.progressPct}% · In progress`
                              : "In progress"}
                          </span>
                        )}
                      </div>
                      {done ? (
                        <CheckCircle2 size={14} className="lp-detail__res-check" />
                      ) : unlocked ? (
                        <span className="lp-detail__res-badge lp-detail__res-badge--unlocked">
                          Unlocked
                        </span>
                      ) : (
                        <span className="lp-detail__res-badge lp-detail__res-badge--locked">
                          <Lock size={10} /> Locked
                        </span>
                      )}
                      {/* SCORM launches in-app (no external href); openable even
                          after completion so students can review it. */}
                      {unlocked && r.kind === "scorm" && (
                        <button
                          type="button"
                          className="lp-detail__res-open"
                          onClick={() => setScormLesson(r.refId)}
                          title={done ? "Review" : "Open"}
                        >
                          {done ? <PlayCircle size={13} /> : <ArrowRight size={13} />}
                        </button>
                      )}
                      {/* Documents open in the in-app viewer (openable even
                          after viewing, so students can re-read materials). */}
                      {unlocked && r.kind === "document" && (
                        <button
                          type="button"
                          className="lp-detail__res-open"
                          onClick={() =>
                            openMaterial({ _id: r.refId, title: r.title, fileType: r.fileType })
                          }
                          title={done ? "Open again" : "Open"}
                        >
                          <ArrowRight size={13} />
                        </button>
                      )}
                      {/* Assignments / quizzes / coding go to their own pages. */}
                      {unlocked && !["scorm", "document"].includes(r.kind) && r.href && !done && (
                        <a
                          href={r.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="lp-detail__res-open"
                        >
                          <ArrowRight size={13} />
                        </a>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* ── Motivational footer ───────────────────────────────────── */}
          {progressPct > 0 && (
            <div className="lp-detail__motivation">
              <span className="lp-detail__motivation-star">⭐</span>
              <span>
                {progressPct >= 100
                  ? "Great job! You've completed this session. Keep up the momentum!"
                  : "You're on a roll. Complete the next session to keep your streak alive."}
              </span>
            </div>
          )}
        </>
      )}

      {/* SCORM player — launched in-app for an unlocked SCORM resource. */}
      <AppModal
        show={!!scormLesson}
        onHide={() => { setScormLesson(null); refreshDetail(); }}
        title="Interactive content"
        icon={<Package size={18} />}
        size="xl"
        closeOnOverlay={false}
        bodyClassName="lp-detail__scorm-body"
      >
        {scormLesson && (
          <div style={{ height: "74vh" }}>
            <ScormPlayer
              // Remount on a SCORM switch so the previous scorm-again instance
              // tears down cleanly (sendBeacon commit on unload) before the
              // next package loads.
              key={scormLesson}
              lessonId={scormLesson}
              onComplete={handleScormComplete}
              hasNextLesson={!!nextScorm}
              onNextLesson={
                nextScorm ? () => setScormLesson(nextScorm.refId) : undefined
              }
              onFinish={() => setScormLesson(null)}
            />
          </div>
        )}
      </AppModal>

      {/* In-app document viewer (rendered once; null until a doc is opened). */}
      {viewer}
    </div>
  );
};

export default InlineSessionDetail;
